import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import AIChatbot from '@/components/AIChatbot';
import { Sparkles, TrendingUp, FileText, Settings, LogOut, Menu, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { getCreditHeroScoreLink, trackAffiliateClick } from '@/utils/affiliate';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [creditScores, setCreditScores] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [scoresRes, analysisRes] = await Promise.all([
        axios.get(`${API}/credit/scores`),
        axios.post(`${API}/ai/analyze-report`)
      ]);
      setCreditScores(scoresRes.data);
      setAnalysis(analysisRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/dashboard')} className="text-violet-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Disputes</button>
              <button onClick={() => navigate('/education')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Learn</button>
              <button onClick={() => navigate('/settings')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Button 
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                className="font-heading"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => navigate('/dashboard')} className="block w-full text-left text-violet-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="block w-full text-left text-slate-600 font-medium">Disputes</button>
              <button onClick={() => navigate('/education')} className="block w-full text-left text-slate-600 font-medium">Learn</button>
              <button onClick={() => navigate('/settings')} className="block w-full text-left text-slate-600 font-medium">Settings</button>
              <Button onClick={handleLogout} variant="ghost" className="w-full justify-start">Logout</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">Welcome back, {user.full_name}!</h1>
          <p className="text-slate-600">Here's your credit health overview</p>
        </div>

        {/* Credit Scores - Bento Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {creditScores?.scores.map((score, index) => (
            <div 
              key={index}
              data-testid={`credit-score-${score.bureau.toLowerCase()}`}
              className="bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="text-sm text-slate-600 mb-2">{score.bureau}</div>
              <div className="text-5xl font-heading font-bold text-gradient mb-2">{score.score}</div>
              <div className="flex items-center gap-2 text-green-600">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm font-medium">+12 this month</span>
              </div>
            </div>
          ))}
        </div>

        {/* Analysis Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Negative Items */}
          <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
            <h3 className="text-xl font-heading font-bold mb-4">Items Affecting Your Score</h3>
            <ul className="space-y-3">
              {analysis?.negative_items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-2"></div>
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Priority Actions */}
          <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-8 text-white">
            <h3 className="text-xl font-heading font-bold mb-4">Priority Actions</h3>
            <ul className="space-y-3">
              {analysis?.priority_actions.map((action, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-white mt-2"></div>
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <button
            data-testid="create-dispute-button"
            onClick={() => navigate('/disputes/create')}
            className="bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left group"
          >
            <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center text-violet-600 mb-4 group-hover:scale-110 transition-transform">
              <FileText className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-heading font-semibold mb-2">Create Dispute</h3>
            <p className="text-sm text-slate-600">Generate AI-powered dispute letters</p>
          </button>

          <button
            onClick={() => navigate('/education')}
            className="bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left group"
          >
            <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center text-green-600 mb-4 group-hover:scale-110 transition-transform">
              <Sparkles className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-heading font-semibold mb-2">Learn More</h3>
            <p className="text-sm text-slate-600">Credit repair education center</p>
          </button>

          <a
            href={getCreditHeroScoreLink()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAffiliateClick(axios, user.id)}
            className="bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left group"
          >
            <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600 mb-4 group-hover:scale-110 transition-transform">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-heading font-semibold mb-2">Credit Monitoring</h3>
            <p className="text-sm text-slate-600">Enhanced monitoring with CreditHeroScore</p>
          </a>
        </div>

        {/* Important Resources Section */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl p-8 text-white hover:shadow-2xl transition-all">
            <h3 className="text-xl font-heading font-bold mb-3">🛡️ Report Identity Theft</h3>
            <p className="text-blue-50 mb-6">
              If you've been a victim of identity theft or found fraudulent accounts on your credit report, 
              report it to the FTC immediately.
            </p>
            <a
              href="https://www.identitytheft.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-blue-600 px-6 py-3 rounded-full font-heading font-semibold hover:bg-blue-50 transition-colors"
            >
              Report to FTC →
            </a>
          </div>

          <div className="bg-gradient-to-br from-violet-500 to-purple-500 rounded-3xl p-8 text-white hover:shadow-2xl transition-all">
            <h3 className="text-xl font-heading font-bold mb-3">📊 Credit Monitoring</h3>
            <p className="text-violet-50 mb-6">
              Get real-time alerts on changes to your credit report and monitor all 3 bureaus with Identity IQ.
            </p>
            <a
              href="https://www.identityiq.com/sc-securepreferred.aspx?offercode=4312985V"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAffiliateClick(axios, user.id)}
              className="inline-block bg-white text-violet-600 px-6 py-3 rounded-full font-heading font-semibold hover:bg-violet-50 transition-colors"
            >
              Join Identity IQ Monitoring →
            </a>
          </div>
        </div>

        <AIChatbot user={user} />
      </div>
    </div>
  );
};

export default Dashboard;